import apiClient from './api'
import { withListDefaults } from '@/lib/listFetch'
import type {
  ApiResponse,
  ApiList,
  ActivityLog,
  Invoice,
  InvoiceDetail,
  CreateInvoiceRequest,
  EditInvoiceRequest,
  InvoiceListQuery,
  InvoiceSummary,
  UpdateNotesRequest,
  BulkSendRequest,
  BulkSendInvoicesResponse,
  BulkSendUnpaidResponse,
  BulkActionRequest,
  BulkActionResult,
  UnpaidByClientRow,
  CreditNote,
  CreditNoteDetail,
  CreateCreditNoteRequest,
  EditCreditNoteRequest,
  CreditNoteListQuery,
  SendDocumentRequest,
  PdfLink,
  ExportLink,
  Documentable,
  QuoteDocument,
  ListQuery,
} from '@/types'

// 1:1 with Schema_Specification Section 4.6 (invoices + credit-notes / 2.6.x).
export const invoicesApi = {
  list: (q: InvoiceListQuery = {}) =>
    apiClient
      .get<ApiList<Invoice>>('/invoices', { params: withListDefaults(q) })
      .then(r => r.data),

  summary: (q: Partial<InvoiceListQuery> = {}) =>
    apiClient
      .get<ApiResponse<InvoiceSummary>>('/invoices/summary', { params: q })
      .then(r => r.data),

  get: (id: string) =>
    apiClient
      .get<ApiResponse<InvoiceDetail>>(`/invoices/${id}`)
      .then(r => r.data),

  history: (id: string, q: ListQuery = {}) =>
    apiClient
      .get<ApiList<ActivityLog>>(`/invoices/${id}/history`, {
        params: withListDefaults(q),
      })
      .then(r => r.data),

  create: (body: CreateInvoiceRequest) =>
    apiClient.post<ApiResponse<Invoice>>('/invoices', body).then(r => r.data),

  update: (id: string, body: EditInvoiceRequest) =>
    apiClient
      .put<ApiResponse<Invoice>>(`/invoices/${id}`, body)
      .then(r => r.data),

  updateNotes: (id: string, body: UpdateNotesRequest) =>
    apiClient
      .put<ApiResponse<InvoiceDetail>>(`/invoices/${id}/notes`, body)
      .then(r => r.data),

  validate: (id: string) =>
    apiClient
      .post<ApiResponse<Invoice>>(`/invoices/${id}/validate`, {})
      .then(r => r.data),

  // Feedback #39: delete a Pro Forma invoice (BE rejects non-Pro Forma).
  remove: (id: string) =>
    apiClient.delete<ApiResponse<null>>(`/invoices/${id}`).then(r => r.data),

  send: (id: string, body: SendDocumentRequest) =>
    apiClient
      .post<ApiResponse<Invoice>>(`/invoices/${id}/send`, body)
      .then(r => r.data),

  bulkSend: (body: BulkSendRequest) =>
    apiClient
      .post<ApiResponse<BulkSendInvoicesResponse>>('/invoices/bulk-send', body)
      .then(r => r.data),

  // 2.6.6: send a reminder for every Overdue invoice (past-due Issued /
  // Partially paid invoices are reconciled to Overdue first; invoices not yet
  // past their due date are skipped). The full set is resolved server-side —
  // the same filter /invoices/unpaid-summary previews — and queued in chunks, so
  // the response only reports how many were queued, not a per-invoice result.
  bulkSendUnpaid: () =>
    apiClient
      .post<
        ApiResponse<BulkSendUnpaidResponse>
      >('/invoices/bulk-send-unpaid', {})
      .then(r => r.data),

  // Feedback #37: bulk row actions.
  bulkChase: (body: BulkActionRequest) =>
    apiClient
      .post<ApiResponse<BulkActionResult>>('/invoices/bulk-chase', body)
      .then(r => r.data),

  bulkMarkPaid: (body: BulkActionRequest) =>
    apiClient
      .post<ApiResponse<BulkActionResult>>('/invoices/bulk-mark-paid', body)
      .then(r => r.data),

  // Bulk export PDFs -> a single ZIP (binary download).
  bulkExportZip: (body: BulkActionRequest) =>
    apiClient
      .post('/invoices/bulk-export-pdf', body, { responseType: 'blob' })
      .then(r => r.data as Blob),

  // Feedback #36: clients with overdue invoices (the exact set
  // bulkSendUnpaid emails, so the confirm modal count matches the send).
  unpaidByClient: () =>
    apiClient
      .get<ApiResponse<UnpaidByClientRow[]>>('/invoices/unpaid-summary')
      .then(r => r.data),

  // Feedback #35: list export honouring the active search/status/date filters.
  export: (q: InvoiceListQuery & { format?: 'xlsx' | 'csv' } = {}) =>
    apiClient
      .get<ApiResponse<ExportLink>>('/invoices/export', { params: q })
      .then(r => r.data),

  pdf: (id: string) =>
    apiClient
      .get<ApiResponse<PdfLink>>(`/invoices/${id}/pdf`)
      .then(r => r.data),

  // --- Attachments (feedback #44) ---
  listDocuments: (id: string, q: ListQuery = {}) =>
    apiClient
      .get<ApiList<QuoteDocument>>(`/invoices/${id}/documents`, {
        params: withListDefaults(q),
      })
      .then(r => r.data),

  uploadDocument: (id: string, file: File, docType = 'invoice') => {
    const form = new FormData()
    form.append('file', file)
    form.append('doc_type', docType)
    return apiClient
      .post<ApiResponse<QuoteDocument>>(`/invoices/${id}/documents`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then(r => r.data)
  },

  attachExistingDocument: (
    id: string,
    fileId: string,
    docType: 'client' | 'library'
  ) =>
    apiClient
      .post<ApiResponse<QuoteDocument>>(`/invoices/${id}/documents`, {
        file_id: fileId,
        doc_type: docType,
      })
      .then(r => r.data),

  renameDocument: (id: string, documentId: string, name: string) =>
    apiClient
      .patch<
        ApiResponse<QuoteDocument>
      >(`/invoices/${id}/documents/${documentId}/rename`, { name })
      .then(r => r.data),

  removeDocument: (id: string, documentId: string) =>
    apiClient
      .delete<ApiResponse<null>>(`/invoices/${id}/documents/${documentId}`)
      .then(r => r.data),
}

// Credit notes (2.6.9-2.6.12).
export const creditNotesApi = {
  list: (q: CreditNoteListQuery = {}) =>
    apiClient
      .get<
        ApiList<CreditNote>
      >('/credit-notes', { params: withListDefaults(q) })
      .then(r => r.data),

  get: (id: string) =>
    apiClient
      .get<ApiResponse<CreditNoteDetail>>(`/credit-notes/${id}`)
      .then(r => r.data),

  create: (body: CreateCreditNoteRequest) =>
    apiClient
      .post<ApiResponse<CreditNote>>('/credit-notes', body)
      .then(r => r.data),

  // Feedback #45: edit / delete draft credit notes (BE PUT/DELETE exist).
  update: (id: string, body: EditCreditNoteRequest) =>
    apiClient
      .put<ApiResponse<CreditNote>>(`/credit-notes/${id}`, body)
      .then(r => r.data),

  remove: (id: string) =>
    apiClient
      .delete<ApiResponse<null>>(`/credit-notes/${id}`)
      .then(r => r.data),

  send: (id: string, body: SendDocumentRequest) =>
    apiClient
      .post<ApiResponse<CreditNote>>(`/credit-notes/${id}/send`, body)
      .then(r => r.data),

  pdf: (id: string) =>
    apiClient
      .get<ApiResponse<PdfLink>>(`/credit-notes/${id}/pdf`)
      .then(r => r.data),
}

// Documentable is re-exported here for callers needing the wire shape.
export type { Documentable }

export default invoicesApi
